import { useNavigate } from "react-router-dom";
import {
  Factory,
  Users,
  Handshake,
  UserRoundCog,
  Share2,
  Languages,
  Tags,
  Speech,
  Film,
  Tag,
  Cog,
  Settings,
  Database,
  Palette,
  Send,
  Sliders,
} from "lucide-react";
import "./Setting.css";
import PageHeader from "@/components/Layout/PageHeader";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../../../store";
import {
  getUserServiceByAdminType,
  ADMIN_TYPE_LEVELS,
  checkCMSPermission,
} from "@/utils/permission";
import { ADMIN_URL } from "@/utils/constants/uiConstants";
import { LucideIcon } from "lucide-react";
import { Tooltip } from "@/components/Tooltip/Tooltip";

type SettingLink = {
  to: string;
  Icon: LucideIcon;
  label: string;
  disabled?: boolean;
  tooltip?: string;
};

const sections = [
  {
    title: "Admin",
    links: [
      // { to: '/admin/organization', Icon: Building, label: 'Organization' },
      { to: "/admin/business", Icon: Factory, label: "Business" },
      {
        to: "/admin/product",
        Icon: Users,
        label: "Teams",
        disabled: true,
        tooltip: "This service moved to admin panel",
      },
      { to: "/admin/agencies", Icon: Handshake, label: "Agency" },
      {
        to: "/users",
        Icon: UserRoundCog,
        label: "Users",
        disabled: true,
        tooltip: "This service moved to admin panel",
      },
      { to: "/socialconnect", Icon: Share2, label: "Social Connect" },
    ] as SettingLink[],
  },
  {
    title: "Presets",
    links: [
      {
        to: "/meta-presets/meta",
        Icon: Database,
        label: "Meta",
        disabled: false,
      },
      {
        to: "/meta-presets/social",
        Icon: Send,
        label: "Publishing",
        disabled: false,
      },
      {
        to: "/meta-presets/processing",
        Icon: Cog,
        label: "Media Processing",
        disabled: true,
      },

      {
        to: "/meta-presets/branding",
        Icon: Palette,
        label: "Brand Package",
        disabled: true,
      },
      {
        to: "/meta-presets/transcoding",
        Icon: Sliders,
        label: "Transcoding",
        disabled: true,
      },
    ] as SettingLink[],
  },
  {
    title: "Manage Meta",
    links: [
      { to: "/meta-managements/language", Icon: Languages, label: "Language" },
      { to: "/meta-managements/category", Icon: Tags, label: "Category" },
      { to: "/meta-managements/cast", Icon: Speech, label: "Cast" },
      { to: "/meta-managements/genre", Icon: Film, label: "Genre" },
      { to: "/meta-managements/tags", Icon: Tag, label: "Tags" },
      // { to: '/meta-managements/manage-listing', Icon: List, label: 'Manage Listing' },
    ] as SettingLink[],
  },
  // {
  //   title: 'Configuration',
  //   links: [
  //     { to: '/configuration/workflows', Icon: Workflow, label: 'Workflows' },
  //     { to: '/configuration/archival-policy', Icon: Archive, label: 'Archival Policy' },
  //     { to: '/configuration/api-config', Icon: Braces, label: 'API Config' },
  //     { to: '/configuration/buckets', Icon: Database, label: 'Buckets' },
  //     { to: '/configuration/feeds', Icon: Rss, label: 'Feeds' },
  //   ],
  // },
];

const Setting = ({ singleCurrentUser }: Props) => {
  const navigate = useNavigate();

  const handleClick = (path: string) => {
    if (path.includes("meta-presets")) {
      let adminUrl = process.env.NX_APP_ADMIN_URL + ADMIN_URL.META_PRESETS;

      if (path.includes("social")) {
        adminUrl = process.env.NX_APP_ADMIN_URL + ADMIN_URL.PUBLISHING_PRESETS;
      } else if (path.includes("meta")) {
        adminUrl = process.env.NX_APP_ADMIN_URL + ADMIN_URL.META_PRESETS;
      }
      window.open(adminUrl, "_blank");
    } else {
      navigate(`${path}?settings`);
    }
  };
  const superAdminType = getUserServiceByAdminType(
    singleCurrentUser?.permissions,
    ADMIN_TYPE_LEVELS.super
  );
  const isSuperAdmin = superAdminType.type === "super";
  const hasCMSPermission = checkCMSPermission(
    singleCurrentUser?.permissions,
    singleCurrentUser?.team
  );
  const getVisibleSections = () => {
    const result = [];

    for (const section of sections) {
      if (section.title === "Admin") {
        if (isSuperAdmin) {
          result.push(section);
        } else if (hasCMSPermission) {
          const socialConnectLinks = section.links.filter(
            (link) => link.to === "/socialconnect"
          );
          if (socialConnectLinks.length > 0) {
            result.push({ ...section, links: socialConnectLinks });
          }
        }
      } else if (section.title === "Manage Meta") {
        if (isSuperAdmin) {
          result.push(section);
        }
      } else if (section.title === "Presets") {
        if (isSuperAdmin) {
          result.push(section);
        }
      } else {
        result.push(section);
      }
    }

    return result;
  };

  const visibleSections = getVisibleSections();

  return (
    <div className="container">
      <PageHeader
        title="SETTINGS"
        icon={<Settings className="no-fill" size={20} strokeWidth={1.2} />}
      />

      <div className="px-4">
        {visibleSections.map((section) => (
          <div className="mt-4 mb-5" key={section.title}>
            <h6 className="text-uppercase">{section.title}</h6>
            <div className="settings-grid mt-3">
              {section.links.map((link: SettingLink) => (
                <Tooltip key={link.to} content={link.tooltip} placement="top">
                  <span
                    style={
                      link.disabled ? { cursor: "not-allowed" } : undefined
                    }
                  >
                    <button
                      onClick={() => {
                        if (link.disabled) {
                          return;
                        }
                        handleClick(link.to);
                      }}
                      className={`card-link setting-item ${
                        link.disabled ? "disabled" : ""
                      }`}
                      type="button"
                      disabled={link.disabled}
                      style={
                        link.disabled ? { pointerEvents: "none" } : undefined
                      }
                    >
                      <link.Icon size={24} strokeWidth={1.5} />
                      <span className="pt-2">{link.label}</span>
                    </button>
                  </span>
                </Tooltip>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  singleCurrentUser: state.users.singleCurrentUser,
});

const connector = connect(mapState);
type Props = ConnectedProps<typeof connector>;

export default connector(Setting);
